import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useStudioUser } from '../../hooks/useStudioUser';
import { useStudioPlan } from '../../hooks/useStudioPlan';
import Notification from '../Notification';
import UpgradePrompt from '../UpgradePrompt';
import ConnectStripeRequired from './ConnectStripeRequired';
import {
  PlusIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';

interface DiscountCodeCoupon {
  id: string;
  percent_off: number | null;
  amount_off: number | null;
  currency: string | null;
  valid: boolean;
}

interface DiscountCode {
  id: string;
  code: string;
  active: boolean;
  times_redeemed: number;
  max_redemptions: number | null;
  expires_at: number | null;
  created: number;
  coupon: DiscountCodeCoupon | null;
}

const DEFAULT_STUDIO_TIMEZONE = 'America/New_York';
const DEFAULT_EXPIRATION_TIME = '23:59';

const formatDiscount = (code: DiscountCode): string => {
  if (code.coupon?.percent_off != null) {
    return `${code.coupon.percent_off}% off`;
  }
  if (code.coupon?.amount_off != null) {
    return `$${(code.coupon.amount_off / 100).toFixed(2)} off`;
  }
  return '—';
};

const formatExpires = (expiresAt: number | null, timezone: string): string => {
  if (!expiresAt) return 'Never';
  const date = new Date(expiresAt * 1000);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: 'short',
  });
};

const minExpirationDate = (timezone: string): string => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
};

const formatTimezoneLabel = (timezone: string): string => {
  try {
    const part = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      timeZoneName: 'long',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return part?.value || timezone;
  } catch {
    return timezone;
  }
};

const formatUses = (code: DiscountCode): string => {
  if (code.max_redemptions != null) {
    return `${code.times_redeemed} / ${code.max_redemptions}`;
  }
  return `${code.times_redeemed}`;
};

const DiscountCodeSettings: React.FC = () => {
  const { getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  const { hasCapability, loading: planLoading, isFreePlan, studio } = useStudioPlan();

  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stripeConnected, setStripeConnected] = useState<boolean | null>(null);
  const [studioTimezone, setStudioTimezone] = useState(DEFAULT_STUDIO_TIMEZONE);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage' as 'percentage' | 'fixed_amount',
    discount_value: '10',
    max_redemptions: '',
    expires_at: '',
    expires_time: DEFAULT_EXPIRATION_TIME,
  });

  const [notification, setNotification] = useState({
    show: false,
    message: '',
    title: '',
    type: 'success' as 'success' | 'error' | 'general',
  });

  const fetchStudioTimezone = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!response.ok) return;
      const data = await response.json();
      if (data?.timezone) {
        setStudioTimezone(data.timezone);
      }
    } catch {
      // Keep default timezone
    }
  }, [getAuthToken]);

  const fetchCodes = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/discount-codes/${studioUser.studio_id}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (data.stripe_connected === false) {
          setStripeConnected(false);
          setCodes([]);
          return;
        }
        throw new Error(data.error || 'Failed to load discount codes');
      }

      setStripeConnected(true);
      setCodes(data.promotion_codes || []);
    } catch (error) {
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : 'Failed to load discount codes',
        title: 'Error',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, studioUser?.studio_id]);

  useEffect(() => {
    if (studioUser?.studio_id) {
      fetchStudioTimezone();
      fetchCodes();
    }
  }, [studioUser?.studio_id, fetchStudioTimezone, fetchCodes]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) return;

    const discountValue =
      formData.discount_type === 'percentage'
        ? parseInt(formData.discount_value, 10)
        : parseFloat(formData.discount_value);

    if (!Number.isFinite(discountValue) || discountValue <= 0) {
      setNotification({
        show: true,
        message: 'Enter a valid discount value.',
        title: 'Validation Error',
        type: 'error',
      });
      return;
    }

    if (formData.discount_type === 'percentage' && discountValue > 100) {
      setNotification({
        show: true,
        message: 'Percentage discount cannot exceed 100%.',
        title: 'Validation Error',
        type: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/discount-codes/${studioUser.studio_id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: formData.code,
            discount_type: formData.discount_type,
            discount_value: discountValue,
            max_redemptions: formData.max_redemptions || null,
            expires_at: formData.expires_at || null,
            expires_time: formData.expires_at ? formData.expires_time : null,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create discount code');
      }

      setCodes((prev) => [data, ...prev]);
      setShowForm(false);
      setFormData({
        code: '',
        discount_type: 'percentage',
        discount_value: '10',
        max_redemptions: '',
        expires_at: '',
        expires_time: DEFAULT_EXPIRATION_TIME,
      });
      setNotification({
        show: true,
        message: `Discount code "${data.code}" created successfully.`,
        title: 'Success',
        type: 'success',
      });
    } catch (error) {
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : 'Failed to create discount code',
        title: 'Error',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (code: DiscountCode) => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) return;

    if (!window.confirm(
      `Delete code "${code.code}"? This removes it from your list. The code must already be deactivated in Stripe.`
    )) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/discount-codes/${studioUser.studio_id}/${code.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete discount code');
      }

      if (!data.deleted) {
        throw new Error('Could not delete this code. Please try again.');
      }

      setCodes((prev) => prev.filter((c) => c.id !== code.id));
      setNotification({
        show: true,
        message: `Code "${code.code}" has been removed from your list.`,
        title: 'Success',
        type: 'success',
      });
    } catch (error) {
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : 'Failed to delete discount code',
        title: 'Error',
        type: 'error',
      });
    }
  };

  const handleDeactivate = async (code: DiscountCode) => {
    const authToken = getAuthToken();
    if (!authToken || !studioUser?.studio_id) return;

    if (!window.confirm(
      `Deactivate code "${code.code}"? It will no longer work at checkout. You can delete it from your list afterward.`
    )) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/discount-codes/${studioUser.studio_id}/${code.id}/deactivate`,
        {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to deactivate discount code');
      }

      setCodes((prev) =>
        prev.map((c) => (c.id === code.id ? { ...c, ...data, active: false } : c))
      );
      setNotification({
        show: true,
        message: `Code "${code.code}" is deactivated in Stripe. You can now delete it from your list if you want.`,
        title: 'Success',
        type: 'success',
      });
    } catch (error) {
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : 'Failed to deactivate discount code',
        title: 'Error',
        type: 'error',
      });
    }
  };

  if (planLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
        </div>
      </div>
    );
  }

  if (isFreePlan || !hasCapability('discountCodes')) {
    return (
      <div className="space-y-6">
        <UpgradePrompt capability="discountCodes" />
      </div>
    );
  }

  if (!studio?.stripe_user_id) {
    return (
      <ConnectStripeRequired
        title="Connect Stripe to create discount codes"
        description="Promotional codes are created in Stripe and used at paid event checkout. Connect your Stripe account under Integrations first, then return here to manage codes."
      />
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Notification
        show={notification.show}
        message={notification.message}
        title={notification.title}
        onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
      />

      <div>
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Discount Codes</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Create promo codes for paid event registration. Codes are managed in Stripe and can be
          entered at checkout. To remove a code, deactivate it first so it stops working, then
          delete it from your list.
        </p>
      </div>

      {codes.length > 0 && (
        <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
          <strong>Removing a code:</strong> First click <strong>Deactivate</strong> to disable it in
          Stripe (customers can no longer use it). Once inactive, click <strong>Delete</strong> to
          remove it from your list.
        </div>
      )}

      <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setShowForm(!showForm)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {showForm ? 'Cancel' : 'New Code'}
            </button>
          </div>

          {showForm && (
            <form
              onSubmit={handleCreate}
              className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 space-y-4"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Code{' '}
                    <span className="font-normal text-gray-500 dark:text-gray-400">
                      — letters and numbers only, no spaces or special characters
                    </span>
                  </label>
                  <input
                    id="code"
                    type="text"
                    required
                    maxLength={50}
                    value={formData.code}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase().replace(/\s/g, ''),
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === ' ') e.preventDefault();
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData
                        .getData('text')
                        .toUpperCase()
                        .replace(/\s/g, '');
                      setFormData((prev) => ({ ...prev, code: pasted.slice(0, 50) }));
                    }}
                    placeholder="Example20"
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm uppercase"
                  />
                </div>

                <div>
                  <label htmlFor="discount_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Discount type
                  </label>
                  <select
                    id="discount_type"
                    value={formData.discount_type}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        discount_type: e.target.value as 'percentage' | 'fixed_amount',
                      }))
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="percentage">Percentage off</option>
                    <option value="fixed_amount">Fixed amount off ($)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="discount_value" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {formData.discount_type === 'percentage' ? 'Percent off' : 'Amount off ($)'}
                  </label>
                  <input
                    id="discount_value"
                    type="text"
                    inputMode={formData.discount_type === 'percentage' ? 'numeric' : 'decimal'}
                    required
                    value={formData.discount_value}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (formData.discount_type === 'percentage') {
                        setFormData((prev) => ({
                          ...prev,
                          discount_value: raw.replace(/\D/g, ''),
                        }));
                        return;
                      }
                      let sanitized = raw.replace(/[^\d.]/g, '');
                      const dotIndex = sanitized.indexOf('.');
                      if (dotIndex !== -1) {
                        sanitized =
                          sanitized.slice(0, dotIndex + 1) +
                          sanitized.slice(dotIndex + 1).replace(/\./g, '');
                      }
                      setFormData((prev) => ({ ...prev, discount_value: sanitized }));
                    }}
                    placeholder={formData.discount_type === 'percentage' ? '10' : '5.00'}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="max_redemptions" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Max uses (optional)
                  </label>
                  <input
                    id="max_redemptions"
                    type="number"
                    min={1}
                    value={formData.max_redemptions}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, max_redemptions: e.target.value }))
                    }
                    placeholder="Unlimited"
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="expires_at" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Expires (optional)
                  </label>
                  <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      id="expires_at"
                      type="date"
                      min={minExpirationDate(studioTimezone)}
                      value={formData.expires_at}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          expires_at: e.target.value,
                          expires_time: e.target.value ? prev.expires_time || DEFAULT_EXPIRATION_TIME : DEFAULT_EXPIRATION_TIME,
                        }))
                      }
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                    <input
                      id="expires_time"
                      type="time"
                      value={formData.expires_time}
                      disabled={!formData.expires_at}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, expires_time: e.target.value }))
                      }
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50 sm:max-w-[10rem]"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Times use your studio timezone ({formatTimezoneLabel(studioTimezone)}).
                    Defaults to 11:59 PM on the selected date.
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {saving ? 'Creating…' : 'Create Code'}
                </button>
              </div>
            </form>
          )}

          {codes.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
              <TicketIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No discount codes</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Create a code for customers to use at event checkout.
              </p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Uses
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Expires
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {codes.map((code) => (
                    <tr key={code.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900 dark:text-white">
                        {code.code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDiscount(code)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatUses(code)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatExpires(code.expires_at, studioTimezone)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            code.active
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {code.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-3">
                          {code.active ? (
                            <button
                              type="button"
                              onClick={() => handleDeactivate(code)}
                              className="font-medium text-amber-700 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-300"
                            >
                              Deactivate
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              Deactivated
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDelete(code)}
                            disabled={code.active}
                            title={
                              code.active
                                ? 'Deactivate this code before deleting it'
                                : 'Remove from your list'
                            }
                            className={`font-medium ${
                              code.active
                                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                : 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                            }`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
    </div>
  );
};

export default DiscountCodeSettings;
